import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';
import { buildTrendSeries } from '../dashboard/digestTrendsModel';

// Phase 6 Part 2c (10/07) — three blocks under Reports → Performance Analytics.
// Every figure is read from an endpoint that already existed; none is computed
// here:
//   status distribution  ← GET /api/reports/portfolio-stats  (status_breakdown)
//   bounce trend         ← GET /api/reports/portfolio-digest?days=200 (Part 1 route)
//   top syndicators      ← GET /api/data-health/top-syndicators (stored position
//                          fields: advance_amount / running_collection /
//                          available_balance — labelled as such)
// Each block has its own empty/error state so one failure never blanks the rest.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  Funded: '#10b981', Current: '#10b981', Active: '#10b981',
  'Payment Issues': '#f59e0b', 'In-House Collection': '#f97316', Default: '#ef4444', Collected: '#3b82f6',
  'Paid in Full': '#8b5cf6', 'Paid in Full (Refinanced)': '#a78bfa', 'Paid in Full / Settled': '#c4b5fd',
};

const Block = ({ title, note, children }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
      {note && <p className="text-xs text-gray-500">{note}</p>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Problem = ({ text }) => <p className="text-sm text-gray-500">{text}</p>;

const AnalyticsExtras = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({ status: 'loading', data: null });
  const [digest, setDigest] = useState({ status: 'loading', data: null });
  const [synd, setSynd] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let cancelled = false;
    const headers = { ...getAuthHeaders(), 'X-Tenant': 'live' };
    const settle = (setter) => (r) => {
      if (cancelled) return;
      if (r.status === 'fulfilled') setter({ status: 'ok', data: r.value.data });
      else setter({ status: 'error', data: null, error: r.reason?.response?.data?.detail || r.reason?.message || 'request failed' });
    };
    Promise.allSettled([
      axios.get(`${API}/reports/portfolio-stats`, { headers }),
      axios.get(`${API}/reports/portfolio-digest`, { headers, params: { days: 200 } }),
      axios.get(`${API}/data-health/top-syndicators`, { headers }),
    ]).then(([a, b, c]) => { settle(setStats)(a); settle(setDigest)(b); settle(setSynd)(c); });
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const statusRows = useMemo(() => {
    const sb = stats.data?.status_breakdown || {};
    return Object.entries(sb)
      .map(([status, count]) => ({ status, count: Number(count) || 0 }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [stats.data]);

  const bounceSeries = useMemo(
    () => buildTrendSeries(digest.data?.reports || [], { months: 6, yesterday: digest.data?.yesterday }),
    [digest.data],
  );
  const syndRows = synd.data?.top_syndicators || [];

  return (
    <div className="space-y-6 mt-6" data-testid="analytics-extras">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Block title="Deal status distribution" note="Deal counts by current status, all non-cancelled deals (portfolio-stats).">
          {stats.status === 'loading' && <Problem text="Loading…" />}
          {stats.status === 'error' && <Problem text={`Couldn't load portfolio stats: ${stats.error}`} />}
          {stats.status === 'ok' && statusRows.length === 0 && <Problem text="No deals to summarise." />}
          {stats.status === 'ok' && statusRows.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.max(180, statusRows.length * 34)}>
              <BarChart data={statusRows} layout="vertical" margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="status" width={170} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [v, 'Deals']} />
                <Bar dataKey="count" name="Deals" radius={[0, 4, 4, 0]}>
                  {statusRows.map((r) => <Cell key={r.status} fill={STATUS_COLORS[r.status] || '#6b7280'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Block>

        <Block
          title="Bounce trend"
          note={bounceSeries.range
            ? `${bounceSeries.rangeLabel} · ${bounceSeries.granularity === 'week' ? 'weekly totals' : 'daily totals'}${bounceSeries.shallow ? ' · history is shorter than 6 months; nothing is back-filled' : ''}`
            : 'ACH returns per day from the portfolio_digest series.'}
        >
          {digest.status === 'loading' && <Problem text="Loading…" />}
          {digest.status === 'error' && <Problem text={`Couldn't load digest history: ${digest.error}`} />}
          {digest.status === 'ok' && !bounceSeries.range && <Problem text="No digest history yet — run portfolio_digest from Admin → Jobs." />}
          {digest.status === 'ok' && bounceSeries.range && (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={bounceSeries.points} margin={{ top: 8, right: 16, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="n" allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                <YAxis yAxisId="usd" orientation="right" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} width={90} />
                <Tooltip formatter={(value, name) => [name === 'Bounced $' ? formatCurrency(value) : value, name]} />
                <Legend />
                <Line yAxisId="n" type="monotone" dataKey="bounces" name="Bounces" stroke="#ef4444" strokeWidth={2} dot={bounceSeries.granularity === 'day'} />
                <Line yAxisId="usd" type="monotone" dataKey="bounced" name="Bounced $" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Block>
      </div>

      <Block title="Top syndicators" note="By capital placed, from stored position fields (advance_amount, running_collection, available_balance). Top 10.">
        {synd.status === 'loading' && <Problem text="Loading…" />}
        {synd.status === 'error' && <Problem text={`Couldn't load syndicators: ${synd.error}`} />}
        {synd.status === 'ok' && syndRows.length === 0 && <Problem text="No syndication positions found." />}
        {synd.status === 'ok' && syndRows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Syndicator</TableHead>
                <TableHead className="text-right">Deals</TableHead>
                <TableHead className="text-right">Invested</TableHead>
                <TableHead className="text-right">Collected</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">ROI</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {syndRows.map((s) => (
                <TableRow key={s.syndicator_id}>
                  <TableCell>{s.syndicator_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.deal_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(s.total_invested)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(s.total_collected)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(s.available_balance)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${(s.roi || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>{(s.roi || 0).toFixed(2)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Block>
    </div>
  );
};

export default AnalyticsExtras;
